import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import './ProfilePage.css';

/**
 * User profile page component
 */
function ProfilePage() {
  const { user, updateSubscription } = useContext(AuthContext);
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Toggle edit mode
  const toggleEdit = () => {
    setIsEditing(!isEditing);
    setError(null);
    setSuccess(null);
  };

  // Toggle password change mode
  const togglePasswordChange = () => {
    setIsChangingPassword(!isChangingPassword);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(null);
  };

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Simulate profile update
      console.log('Updating profile:', { name, email });
      
      // Show success message and exit edit mode
      setTimeout(() => {
        setSuccess('Profile updated successfully');
        setIsEditing(false);
        setIsLoading(false);
      }, 1000);
      
    } catch (err) {
      console.error('Profile update error:', err);
      setError('Failed to update profile');
      setIsLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Simulate password change
      console.log('Changing password');
      
      // Show success message and exit password change mode
      setTimeout(() => {
        setSuccess('Password changed successfully');
        setIsChangingPassword(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setIsLoading(false);
      }, 1000);
      
    } catch (err) {
      console.error('Password change error:', err);
      setError('Failed to change password');
      setIsLoading(false);
    }
  };

  // Handle subscription upgrade
  const handleUpgradeSubscription = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Simulate subscription upgrade
      console.log('Upgrading subscription');
      
      // Show success message
      setTimeout(() => {
        setSuccess('Subscription upgraded successfully');
        setIsLoading(false);
      }, 1000);
      
    } catch (err) {
      console.error('Subscription upgrade error:', err);
      setError('Failed to upgrade subscription');
      setIsLoading(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1>Your Profile</h1>
          <Link to="/" className="back-link">
            &larr; Back to Home
          </Link>
        </div>
        
        {error && (
          <div className="profile-error">
            {error}
          </div>
        )}
        
        {success && (
          <div className="profile-success">
            {success}
          </div>
        )}
        
        <div className="profile-section">
          <h2>Personal Information</h2>
          
          {isEditing ? (
            <form onSubmit={handleProfileUpdate} className="profile-form">
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading || true} // Email editing disabled
                  required
                />
                <small className="form-note">Email cannot be changed</small>
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={toggleEdit}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div className="profile-info">
              <div className="info-row">
                <div className="info-label">Name</div>
                <div className="info-value">{user?.name || 'Guest User'}</div>
              </div>
              <div className="info-row">
                <div className="info-label">Email</div>
                <div className="info-value">{user?.email || 'guest@example.com'}</div>
              </div>
              
              <button
                type="button"
                className="edit-button"
                onClick={toggleEdit}
              >
                Edit Profile
              </button>
            </div>
          )}
        </div>
        
        <div className="profile-section">
          <h2>Security</h2>
          
          {isChangingPassword ? (
            <form onSubmit={handlePasswordChange} className="profile-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={togglePasswordChange}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          ) : (
            <div className="profile-info">
              <div className="info-row">
                <div className="info-label">Password</div>
                <div className="info-value">••••••••</div>
              </div>
              
              <button
                type="button"
                className="edit-button"
                onClick={togglePasswordChange}
              >
                Change Password
              </button>
            </div>
          )}
        </div>
        
        <div className="profile-section">
          <h2>Subscription</h2>
          
          <div className="profile-info">
            <div className="info-row">
              <div className="info-label">Current Plan</div>
              <div className="info-value">
                {user?.subscription === 'premium' ? (
                  <span className="premium-badge">Premium</span>
                ) : (
                  <span>Free</span>
                )}
              </div>
            </div>
            
            {user?.subscription !== 'premium' && (
              <button
                type="button"
                className="upgrade-button"
                onClick={handleUpgradeSubscription}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Upgrade to Premium'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;